/**
 * ClientHero Component
 * Hero section for consultant-focused client page
 * Direct, execution-focused messaging without marketing fluff
 */

import React, { useState, useEffect } from 'react';

export const ClientHero: React.FC = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const scrollToForm = () => {
    const element = document.getElementById('execution-fit-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section className="relative min-h-screen bg-stone-50 overflow-hidden">
      {/* Minimal decorative elements */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `linear-gradient(to right, rgb(28, 25, 23) 1px, transparent 1px),
                          linear-gradient(to bottom, rgb(28, 25, 23) 1px, transparent 1px)`,
        backgroundSize: '80px 80px'
      }} />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="min-h-screen flex items-center py-20 pt-32">
          <div className="max-w-4xl mx-auto">

            {/* Main Headline */}
            <h1
              className={`font-display text-5xl md:text-6xl lg:text-7xl text-stone-900 leading-[1.15] mb-8 transition-all duration-700 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              When leadership alignment doesn't translate into execution, consultants lose credibility.
            </h1>

            {/* Body Text */}
            <div
              className={`space-y-6 text-xl md:text-2xl text-stone-700 leading-relaxed mb-12 transition-all duration-700 delay-100 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <p>
                You help leaders clarify priorities and direction.
              </p>
              <p>
                But once the conversations end, execution weakens, visibility drops, and follow-ups become manual.
              </p>
              <p className="text-stone-900 font-medium">
                The work was right.<br />
                The execution environment wasn't.
              </p>
            </div>

            {/* CTA */}
            <div
              className={`transition-all duration-700 delay-200 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <button
                onClick={scrollToForm}
                className="group inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white bg-stone-900 rounded-lg hover:bg-stone-800 transition-all duration-300 hover:-translate-y-0.5 shadow-lg hover:shadow-xl"
              >
                Explore if this fits your execution approach
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <div className="flex flex-col items-center gap-2 text-stone-400">
          <div className="w-6 h-10 border-2 border-current rounded-full flex justify-center p-2">
            <div className="w-1 h-2 bg-current rounded-full animate-bounce" />
          </div>
        </div>
      </div>
    </section>
  );
};
